// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import { clientLogger } from '@seedplanner/logger';
import {
  SOW_DENSITY_UNITS_VALUES,
  STEP_NAME,
  type WeightUnit,
  getWeightUnitOptions,
  isPlantStep,
  utils,
} from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '../../lib/base';
import { Help } from '../../lib/help';
import { AddCircleOutlineIcon } from '../../lib/icons';
import { CROP_NAMES_QUERY } from '../../queries/CropNamesQuery';
import { CROPS_QUERY } from '../../queries/CropsQueries';
import { SINGLE_CROP_QUERY } from '../../queries/SingleCropQuery';
import type {
  CreateCropMutation,
  CreateCropMutationVariables,
  SingleCropQuery,
  SingleCropQueryVariables,
} from '../../types/__generated__/graphql';
import {
  createOnBlurRequired,
  getDaysFromSeconds,
  getNumericID,
  getSecondsFromDaysString,
  getSecondsFromDaysStringRequired,
  shortenWeightUnit,
  validateNumInput,
} from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import NewCropStepForm, {
  type CropStepFormInput,
  type Props as NewCropStepFormProps,
} from '../cropStep/NewCropStepForm';
import NewSizeForm, { type SizeFormInput, type Props as NewSizeFormProps } from '../size/NewSizeForm';

// Need to update these queries: order form, crops home
const ADD_CROP_MUTATION = gql`
  mutation CreateCrop(
    $crop_name: String!
    $yield_per_tray: Float!
    $yield_unit: String!
    $notes: String
    $total_grow_time: Int!
    $max_hold_time: Int!
    $harvest_buffer_time: Int
    $sow_density: Float
    $sow_density_unit: String!
    $create_crop_steps: [CreateCropCropStepInput!]!
    $sizes: [CreateProductSizeInput!]!
    $buffer_percentage: Int!
  ) {
    createCrop(
      input: {
        crop_name: $crop_name
        yield_per_tray: $yield_per_tray
        yield_unit: $yield_unit
        total_grow_time: $total_grow_time
        notes: $notes
        max_hold_time: $max_hold_time
        harvest_buffer_time: $harvest_buffer_time
        sow_density: $sow_density
        sow_density_unit: $sow_density_unit
        create_crop_steps: $create_crop_steps
        sizes: $sizes
        buffer_percentage: $buffer_percentage
      }
    ) {
      new_crop {
        id
        crop_name
        total_grow_time
      }
    }
  }
`;

interface Props {
  readonly cropId?: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function AddCropDialog({ isOpen, onClose, onSuccess, cropId }: Props) {
  const [fetchCrop, { data: cropData, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    SingleCropQuery,
    SingleCropQueryVariables
  >(SINGLE_CROP_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [addCrop, { loading: addCropLoading }] = useMutation<CreateCropMutation, CreateCropMutationVariables>(
    ADD_CROP_MUTATION,
  );
  const { preferredWeightUnit } = useUserContext();

  const isForCloneCrop = cropId !== undefined;
  const secondsToDays = React.useCallback((seconds: number) => seconds / 86400, []);

  React.useEffect(() => {
    if (isOpen && cropId !== undefined) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            const cropName = crop.crop_name;
            const cropGrowDuration = secondsToDays(crop.total_grow_time).toString();
            const maxHoldTime = crop.max_hold_time != null ? getDaysFromSeconds(crop.max_hold_time).toString() : '0';
            const harvestBufferTime =
              crop.harvest_buffer_time != null ? getDaysFromSeconds(crop.harvest_buffer_time).toString() : '';
            const sowDensity = crop.sowing_density ?? '';
            const sowDensityUnit = crop.sow_density_unit ?? preferredWeightUnit;
            const yieldPerTray = crop.yield_per_tray;
            const yieldUnit = crop.yield_unit;
            const notes = crop.notes ?? '';
            const sizeForms = crop.edge_sizes.edges.map(({ node: size }) => ({
              size: size.size.toString(),
              unit: size.unit as WeightUnit,
              price: size.price_per_item == null ? '' : size.price_per_item,
              priceValidation: '',
              sizeValidation: '',
            }));
            const cropStepForms = crop.steps.edges.map(({ node }) => ({
              maybeExistingId: getNumericID(node.id),
              name: node.name,
              timeToStartAfterFirstStep:
                node.name === STEP_NAME.HARVEST
                  ? cropGrowDuration
                  : secondsToDays(node.time_to_start_after_first_step).toString(),
              description: node.description ?? '',
              nameValidation: '',
              timeToStartAfterFirstStepValidation: '',
            }));

            setCropName(cropName);
            setDuration(cropGrowDuration);
            setMaxHoldTime(maxHoldTime);
            setHarvestBufferTime(harvestBufferTime);
            setSowDensity(sowDensity);
            setSowDensityUnit(sowDensityUnit as WeightUnit);
            setYieldPerTray(yieldPerTray);
            setYieldUnit(yieldUnit as WeightUnit);
            setBufferPercent(crop.buffer_percentage.toString());
            setNotes(notes);
            setSizeForms(
              sizeForms.map((values, idx) => {
                const formKey = utils.getRandomInt();
                return (
                  <NewSizeForm
                    key={formKey}
                    formKey={formKey}
                    values={values}
                    index={idx}
                    disableFields={disableFields}
                    onChangeField={handleSizeFormInputChange}
                    onRemove={removeSizeForm}
                  />
                );
              }),
            );
            cropStepForms.sort(
              (a, b) =>
                getSecondsFromDaysStringRequired(a.timeToStartAfterFirstStep) -
                getSecondsFromDaysStringRequired(b.timeToStartAfterFirstStep),
            );
            const formsHavePlantStep = crop.steps.edges.some(({ node }) => isPlantStep(node.name));
            setFormsHavePlantStep(formsHavePlantStep);
            setCropStepForms(
              cropStepForms.map((values, idx) => {
                const formKey = utils.getRandomInt();
                return (
                  <NewCropStepForm
                    key={formKey}
                    formKey={formKey}
                    values={values}
                    index={idx}
                    disableFields={disableFields}
                    onChangeField={handleCropStepFormInputChange}
                    onRemove={removeCropStepForm}
                    formsHavePlantStep={formsHavePlantStep}
                  />
                );
              }),
            );
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, preferredWeightUnit, fetchCrop, secondsToDays]);

  const disableFields = addCropLoading || cropDataLoading;

  const [formsHavePlantStep, setFormsHavePlantStep] = React.useState(false);
  const [cropGrowDuration, setDuration] = React.useState('');
  const [cropName, setCropName] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [sowDensity, setSowDensity] = React.useState('');
  const [unit, setSowDensityUnit] = React.useState<WeightUnit>(preferredWeightUnit);
  const [maxHoldTime, setMaxHoldTime] = React.useState('');
  const [harvestBufferTime, setHarvestBufferTime] = React.useState('');
  const [yieldPerTray, setYieldPerTray] = React.useState('');
  const [yieldUnit, setYieldUnit] = React.useState<WeightUnit>(preferredWeightUnit);
  const [sizeForms, setSizeForms] = React.useState<React.ReactElement<NewSizeFormProps>[]>([]);
  const [cropStepForms, setCropStepForms] = React.useState<React.ReactElement<NewCropStepFormProps>[]>(() => {
    const randomInt = utils.getRandomInt();
    return [
      <NewCropStepForm
        key={randomInt}
        formKey={randomInt}
        values={{
          name: STEP_NAME.HARVEST,
          nameValidation: '',
          timeToStartAfterFirstStep: '',
          timeToStartAfterFirstStepValidation: '',
          description: '',
        }}
        formsHavePlantStep={false}
        index={0}
        disableFields={disableFields}
        onChangeField={handleCropStepFormInputChange}
        onRemove={removeCropStepForm}
        // onEnter={onEnter}
      />,
    ];
  });
  const [bufferPercent, setBufferPercent] = React.useState('');

  const [cropGrowDurationValidation, setDurationValidation] = React.useState('');
  const durationValidationMessage =
    'Crop step start time cannot be larger than total grow time. Change total grow time or crop step time.';
  const onDurationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDuration(e.target.value);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setDurationValidation(msg),
      isRequired: true,
    });
  };
  const onDurationBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    const secondsAsNum = Math.max(0, Number.parseFloat(newVal));
    const time = secondsAsNum.toString();
    setDuration(time);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setDurationValidation(msg),
      isRequired: true,
    });

    // Now validate that total grow time is greater than all cropStep times
    // For any cropStepForm that is greater than total grow time, set the error on that form
    setCropStepForms((prevForms) => {
      const updatedForms = [...prevForms];

      return updatedForms.map((form) =>
        React.cloneElement(form, {
          values: {
            ...form.props.values,
            timeToStartAfterFirstStepValidation:
              Number.parseFloat(form.props.values.timeToStartAfterFirstStep) > secondsAsNum
                ? durationValidationMessage
                : '',
          },
        }),
      );
    });
  };

  const addSizeForm = () => {
    setSizeForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        ...prevForms,
        <NewSizeForm
          index={prevForms.length}
          key={formKey}
          formKey={formKey}
          disableFields={disableFields}
          values={{
            size: '',
            unit: preferredWeightUnit,
            price: '',
            priceValidation: '',
            sizeValidation: '',
          }}
          onChangeField={handleSizeFormInputChange}
          onRemove={removeSizeForm}
          // onEnter={onEnter}
        />,
      ];
    });
  };
  const removeSizeForm = (index: number) => {
    setSizeForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms.splice(index, 1);
      return updatedForms.map((old, idx) => React.cloneElement(old, { index: idx }));
    });
  };
  const handleSizeFormInputChange = (
    index: number,
    fieldName: keyof SizeFormInput,
    value: string | number | null,
    validationFieldName?: 'sizeValidation' | 'unit' | 'priceValidation',
  ) => {
    setSizeForms((prevForms) => {
      const updatedForms = [...prevForms];
      let newValues = { ...updatedForms[index].props.values, [fieldName]: value };
      if (validationFieldName !== undefined && value !== '') {
        newValues = { ...newValues, [validationFieldName]: value };
      }
      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      return updatedForms;
    });
  };

  const [nameValidation, setNameValidation] = React.useState('');

  const onUnitChange = (event: SelectChangeEvent<string>) => {
    setSowDensityUnit(event.target.value as WeightUnit);
  };
  const onChangeSowDensity = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSowDensity(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  };
  const [sowDensityValidation, setSowDensityValidation] = React.useState('');
  const onSowDensityBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  };
  const [maxHoldTimeValidation, setMaxHoldTimeValidation] = React.useState('');
  const onChangeMaxHoldTime = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMaxHoldTime(e.target.value);
    // If harvestBufferTime is greater than maxHoldTime, set validation harvestBufferTimeValidation
    const harvestBufferTimeAsNum = Number.parseFloat(harvestBufferTime);
    const maxHoldTimeAsNum = Number.parseFloat(e.target.value);
    if (harvestBufferTimeAsNum > maxHoldTimeAsNum) {
      setMaxHoldTimeValidation('Shelf life must be greater than or equal to harvest buffer time');
    } else {
      setHarvestBufferTimeValidation('');
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMaxHoldTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };
  const onMaxHoldTimeBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // If harvestBufferTime is greater than maxHoldTime, set validation harvestBufferTimeValidation
    const harvestBufferTimeAsNum = Number.parseFloat(harvestBufferTime);
    const maxHoldTimeAsNum = Number.parseFloat(e.target.value);
    if (harvestBufferTimeAsNum > maxHoldTimeAsNum) {
      setMaxHoldTimeValidation('Shelf life must be greater than or equal to harvest buffer time');
    } else {
      setHarvestBufferTimeValidation('');
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMaxHoldTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };
  const [bufferPercentValidation, setBufferPercentValidation] = React.useState('');
  const onChangeBufferPercent = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBufferPercent(e.target.value);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setBufferPercentValidation(msg),
      validateGTE: 0,
    });
  };
  const onBufferPercentBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setBufferPercentValidation(msg),
      validateGTE: 0,
    });
  };
  const [harvestBufferTimeValidation, setHarvestBufferTimeValidation] = React.useState('');
  const onChangeHarvestBufferTime = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setHarvestBufferTime(e.target.value);
    // If harvestBufferTime is greater than maxHoldTime, set validation harvestBufferTimeValidation
    const harvestBufferTimeAsNum = Number.parseFloat(e.target.value);
    const maxHoldTimeAsNum = Number.parseFloat(maxHoldTime) || 0;
    if (harvestBufferTimeAsNum > maxHoldTimeAsNum) {
      setHarvestBufferTimeValidation('Harvest buffer time must be less than or equal to shelf life');
    } else {
      setMaxHoldTimeValidation('');
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setHarvestBufferTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };
  const onHarvestBufferTimeBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // If harvestBufferTime is greater than maxHoldTime, set validation harvestBufferTimeValidation
    const harvestBufferTimeAsNum = Number.parseFloat(e.target.value);
    const maxHoldTimeAsNum = Number.parseFloat(maxHoldTime) || 0;
    if (harvestBufferTimeAsNum > maxHoldTimeAsNum) {
      setHarvestBufferTimeValidation('Harvest buffer time must be less than or equal to shelf life');
    } else {
      setMaxHoldTimeValidation('');
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setHarvestBufferTimeValidation(msg),
        validateGTE: 0,
        validateInt: true,
      });
    }
  };
  const [yieldPerTrayValidation, setYieldPerTrayValidation] = React.useState('');
  const onChangeYieldPerTray = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setYieldPerTrayValidation(msg),
      validateGT: 0,
      isRequired: true,
    });
    setYieldPerTray(e.target.value);
  };
  const onYieldPerTrayBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setYieldPerTrayValidation(msg),
      validateGT: 0,
      isRequired: true,
    });
  };
  const onYieldUnitChange = (event: SelectChangeEvent<string>) => {
    setYieldUnit(event.target.value as WeightUnit);
  };
  const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCropName(e.target.value);
    if (e.target.value !== '') {
      setNameValidation('');
    }
  };
  const onChangeNotes = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };
  const onNameBlur = createOnBlurRequired('Name is required', setNameValidation);

  const addCropStepForm = () => {
    setCropStepForms((prevForms) => {
      const formKey = utils.getRandomInt();
      return [
        <NewCropStepForm
          key={formKey}
          formKey={formKey}
          index={0}
          disableFields={disableFields}
          values={{
            name: '',
            nameValidation: '',
            timeToStartAfterFirstStep: '',
            description: '',
            timeToStartAfterFirstStepValidation: '',
          }}
          formsHavePlantStep={formsHavePlantStep}
          onChangeField={handleCropStepFormInputChange}
          onRemove={removeCropStepForm}
          // onEnter={onEnter}
        />,
        ...prevForms.map((form, idx) => React.cloneElement(form, { index: idx + 1 })),
      ];
    });
  };

  function removeCropStepForm(index: number) {
    setCropStepForms((prevForms) => {
      const updatedForms = [...prevForms];
      const formRemoved = updatedForms.splice(index, 1);
      let plantStepRemoved = false;
      // If the removed form was a plant step, then set formsHavePlantStep to false
      if (formRemoved.length > 0) {
        plantStepRemoved = isPlantStep(formRemoved[0].props.values.name);
      }
      if (plantStepRemoved) {
        setFormsHavePlantStep(false);
      }

      const final = updatedForms.map((old, idx) => {
        return React.cloneElement(old, {
          index: idx, // Need to update the indexes
          formsHavePlantStep: plantStepRemoved ? false : old.props.formsHavePlantStep,
        });
      });

      return final;
    });
  }
  React.useEffect(() => {
    // Every time cropGrowDuration changes
    // Re-create all the cropStepForms to give them new handleCropStepFormInputChange function
    // Also update the input for the harvest step
    setCropStepForms((prevForms) => {
      const updatedForms = [...prevForms];
      return updatedForms.map((form) => {
        const name = form.props.values.name;

        return React.cloneElement(form, {
          ...form.props,
          values: {
            ...form.props.values,
            timeToStartAfterFirstStep:
              name === STEP_NAME.HARVEST ? cropGrowDuration : form.props.values.timeToStartAfterFirstStep,
          },
          onChangeField: handleCropStepFormInputChange,
        });
      });
    });
  }, [cropGrowDuration]);

  function handleCropStepFormInputChange(
    index: number,
    fieldName: keyof CropStepFormInput,
    value: string,
    validationFieldName?: 'timeToStartAfterFirstStepValidation' | 'nameValidation',
    validationValue?: string,
  ) {
    setCropStepForms((prevForms) => {
      // If new value is a plant step then set to true
      // If new value is not a plant step AND prev value WAS a plant step then set to false
      let newFormsHavePlantStep = formsHavePlantStep;
      if (fieldName === 'name') {
        const prevValue = prevForms[index].props.values[fieldName];
        const newIsPlant = isPlantStep(value);
        if (newIsPlant) {
          newFormsHavePlantStep = true;
        } else if (prevValue !== undefined && isPlantStep(prevValue) && !isPlantStep(value)) {
          newFormsHavePlantStep = false;
        }
      }
      setFormsHavePlantStep(newFormsHavePlantStep);

      const updatedForms = [...prevForms];

      const newValues = {
        ...prevForms[index].props.values,
        [fieldName]: value,
      };
      if (validationFieldName !== undefined && validationValue !== undefined) {
        newValues[validationFieldName] = validationValue;
      }
      if (fieldName === 'timeToStartAfterFirstStepValidation') {
        const seconds = getSecondsFromDaysStringRequired(value);
        const growTimeSeconds = getSecondsFromDaysStringRequired(cropGrowDuration);
        if (seconds > growTimeSeconds) {
          newValues.timeToStartAfterFirstStepValidation = durationValidationMessage;
        } else if (!Number.isInteger(Number(value))) {
          newValues.timeToStartAfterFirstStepValidation = 'When to start must be an integer number of days';
        } else {
          newValues.timeToStartAfterFirstStepValidation = '';
        }
      }

      updatedForms[index] = React.cloneElement(updatedForms[index], {
        values: newValues,
      });

      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const finalForms: React.ReactElement<any, string | React.JSXElementConstructor<any>>[] = updatedForms.map(
        (oldForm) => {
          return React.cloneElement(oldForm, {
            formsHavePlantStep: newFormsHavePlantStep,
          });
        },
      );

      return finalForms;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    const cropSteps = cropStepForms.map((form) => {
      const { name, timeToStartAfterFirstStep, description } = form.props.values;
      return {
        existing_id: form.props.values.maybeExistingId,
        name,
        time_to_start_after_first_step: getSecondsFromDaysStringRequired(timeToStartAfterFirstStep),
        description: description === '' ? null : description,
      };
    });
    const variables = {
      crop_name: cropName,
      total_grow_time: getSecondsFromDaysStringRequired(cropGrowDuration),
      yield_per_tray: Number.parseFloat(yieldPerTray),
      yield_unit: yieldUnit,
      max_hold_time: getSecondsFromDaysString(maxHoldTime, true) ?? 0,
      harvest_buffer_time: getSecondsFromDaysString(harvestBufferTime, true),
      sow_density: sowDensity === '' ? undefined : Number.parseFloat(sowDensity),
      sow_density_unit: unit,
      notes,
      create_crop_steps: cropSteps,
      sizes: sizeForms.map((form) => ({
        size: Number.parseFloat(form.props.values.size),
        unit: form.props.values.unit,
        price: form.props.values.price === '' ? null : Number.parseFloat(form.props.values.price),
      })),
      buffer_percentage: bufferPercent === '' ? 0 : Number.parseInt(bufferPercent),
    };
    void addCrop({
      refetchQueries: [{ query: CROPS_QUERY }, { query: CROP_NAMES_QUERY }],
      variables,
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.createCrop != null) {
          enqueueSnackbar(`Added crop ${dataIn.createCrop.new_crop.crop_name}`, { variant: 'success' });

          setCropName('');
          setDuration('');
          setNotes('');
          setNameValidation('');
          setDurationValidation('');
          setSowDensity('');
          setSowDensityValidation('');
          setSowDensityUnit(preferredWeightUnit);
          setMaxHoldTime('');
          setMaxHoldTimeValidation('');
          setHarvestBufferTime('');
          setHarvestBufferTimeValidation('');
          setYieldPerTray('');
          setYieldPerTrayValidation('');
          setCropStepForms([]);
          setFormsHavePlantStep(false);
          setSizeForms([]);
          setBufferPercent('');
          setBufferPercentValidation('');
          onSuccess();
        } else {
          enqueueSnackbar('Error adding crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'new_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean =
    cropGrowDurationValidation !== '' ||
    nameValidation !== '' ||
    sowDensityValidation !== '' ||
    maxHoldTimeValidation !== '' ||
    yieldPerTrayValidation !== '' ||
    harvestBufferTimeValidation !== '' ||
    bufferPercentValidation !== '' ||
    cropStepForms.some(
      ({ props }) => props.values.nameValidation !== '' || props.values.timeToStartAfterFirstStepValidation !== '',
    ) ||
    sizeForms.some((form) => form.props.values.sizeValidation !== '' || form.props.values.priceValidation !== '');
  const requiredFieldsFilled: boolean =
    cropName !== '' &&
    cropGrowDuration !== '' &&
    yieldPerTray !== '' &&
    sizeForms.every((form) => form.props.values.size !== '') &&
    cropStepForms.every(({ props }) => props.values.name !== '' && props.values.timeToStartAfterFirstStep !== '');
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };
  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="crop-name"
        id="crop-name"
        label="Crop Name"
        value={cropName}
        onChange={onChangeName}
        error={nameValidation !== ''}
        subtext={nameValidation}
        onBlur={onNameBlur}
        onEnter={onEnter}
        hasSubtext
        required
        disabled={disableFields}
      />
      <TextField
        aria-describedby="grow-duration"
        id="grow-duration"
        label="Total Grow Time (days)"
        value={cropGrowDuration || ''}
        type="number"
        numberMin={0}
        onChange={onDurationChange}
        onBlur={onDurationBlur}
        onEnter={onEnter}
        subtext={cropGrowDurationValidation}
        error={cropGrowDurationValidation !== ''}
        inputMode="numeric"
        hasSubtext
        required
        disabled={disableFields}
      />
      <Grid display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        <Grid flexGrow="1">
          <TextField
            aria-describedby="expected-yield"
            id="expected-yield"
            label={`Expected Yield (${yieldUnit} per tray)`}
            value={yieldPerTray}
            onChange={onChangeYieldPerTray}
            onBlur={onYieldPerTrayBlur}
            onEnter={onEnter}
            subtext={yieldPerTrayValidation}
            error={yieldPerTrayValidation !== ''}
            type="number"
            inputMode="decimal"
            required
            numberMin={0}
            hasSubtext
            disabled={disableFields}
          />
        </Grid>
        <Grid>
          <FormControl fullWidth>
            <InputLabel id="yield-units-selector">Units</InputLabel>
            <Select
              aria-describedby="yield-units-selector"
              id="yield-units-selector"
              labelId="yield-units-selector"
              label="Units"
              value={yieldUnit}
              onChange={onYieldUnitChange}
              // onEnter={onEnter}
              required={true}
              disabled={disableFields}
            >
              {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                return (
                  <MenuItem key={sizeUnit} value={sizeUnit}>
                    {shortenWeightUnit(sizeUnit)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid display="flex" flexDirection="row" flexWrap="nowrap" gap={2}>
        <Grid flexGrow="1">
          <TextField
            aria-describedby="sow-density"
            id="sow-density"
            label={`Sow Density (${unit} per tray)`}
            value={sowDensity}
            onChange={onChangeSowDensity}
            onBlur={onSowDensityBlur}
            onEnter={onEnter}
            subtext={sowDensityValidation}
            error={sowDensityValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            hasSubtext
            disabled={disableFields}
          />
        </Grid>
        <Grid>
          <FormControl fullWidth>
            <InputLabel id="units-selector">Units</InputLabel>
            <Select
              aria-describedby="units-selector"
              id="units-selector"
              labelId="units-selector"
              label="Units"
              value={unit}
              onChange={onUnitChange}
              // onEnter={onEnter}
              required={true}
              disabled={disableFields}
            >
              {SOW_DENSITY_UNITS_VALUES.map((sizeUnit) => {
                return (
                  <MenuItem key={sizeUnit} value={sizeUnit}>
                    {shortenWeightUnit(sizeUnit)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="safety-margin"
          id="safety-margin"
          label="Safety Margin (%)"
          value={bufferPercent}
          onChange={onChangeBufferPercent}
          onBlur={onBufferPercentBlur}
          onEnter={onEnter}
          subtext={bufferPercentValidation}
          error={bufferPercentValidation !== ''}
          type="number"
          inputMode="numeric"
          numberMin={0}
          hasSubtext
          disabled={disableFields}
        />
        %
        <Help tooltip="Microgreen Manager will create this much extra for this crop for every tray needed to fulfill an order. So if safety margin is 30%, and an order will need 10.0 trays for this crop for the given inputs of that order, then Microgreen Manager will add an extra 3.0 trays ontop of that" />
      </Box>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="shelf-life"
          id="shelf-life"
          label="Shelf Life (days)"
          value={maxHoldTime}
          onChange={onChangeMaxHoldTime}
          onBlur={onMaxHoldTimeBlur}
          onEnter={onEnter}
          subtext={maxHoldTimeValidation}
          error={maxHoldTimeValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
          disabled={disableFields}
        />
        <Help tooltip="This is how long you can hold this crop after harvest before it can no longer be sold" />
      </Box>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="harvest-buffer-time"
          id="harvest-buffer-time"
          label="Harvest Buffer Time (days)"
          value={harvestBufferTime}
          onChange={onChangeHarvestBufferTime}
          onBlur={onHarvestBufferTimeBlur}
          onEnter={onEnter}
          subtext={harvestBufferTimeValidation}
          error={harvestBufferTimeValidation !== ''}
          type="number"
          inputMode="numeric"
          numberMin={0}
          hasSubtext
          disabled={disableFields}
        />
        <Help tooltip="This is how many days before the order delivery date that this crop should be harvested. For example, if your order delivery date is on Friday, but you want this crop to be harvested on Thursday, then you would set the harvest buffer time to 1 day. This is optional though and defaults to zero." />
      </Box>
      <TextField
        aria-describedby="crop-notes"
        id="crop-notes"
        label="Notes"
        value={notes}
        onChange={onChangeNotes}
        onEnter={onEnter}
        disabled={disableFields}
      />
      {sizeForms.map((sizeForm) => (
        <Box key={sizeForm.props.formKey}>{sizeForm}</Box>
      ))}
      <Grid container display="flex" flexDirection="row" alignItems="center">
        <Tooltip
          title="Add a container size to your crop. This makes it convenient to create orders by container size later."
          placement="bottom-end"
        >
          <Button
            variant="outlined"
            arial-label="add-crop-container-size"
            onClick={addSizeForm}
            disabled={addCropLoading}
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Product
          </Button>
        </Tooltip>
        <Grid flexGrow="1" />
      </Grid>
      {cropStepForms.map((cropStepForm) => (
        <Box key={cropStepForm.props.formKey}>{cropStepForm}</Box>
      ))}
      <Box>
        <Grid container display="flex" flexDirection="row" alignItems="center">
          <Tooltip
            title="Add steps to the process of harvesting your crop. Like plant, water, harvest, etc. You can add steps now or later. If you don't create a plant step or harvest step they will be created automatically."
            placement="bottom-end"
          >
            <Button
              variant="outlined"
              arial-label="add-crop-grow-step"
              onClick={addCropStepForm}
              disabled={addCropLoading}
              startIcon={<AddCircleOutlineIcon />}
            >
              Add Grow Step
            </Button>
          </Tooltip>
          <Grid flexGrow="1" />
        </Grid>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{cropId === undefined || isForCloneCrop ? 'New Crop' : 'Edit Crop'}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {cropId === undefined ? (
          form
        ) : dataError ? (
          <ErrorView error={dataError} />
        ) : cropDataLoading || cropData?.crop == null ? (
          <PageLoading />
        ) : (
          form
        )}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          disabled={disableFields}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
